import React from 'react'
import { css } from '@emotion/core'
import { ContentCompose } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { colors, Paragraph, WhiteTheme } from '@fs/zion-ui'
import TextPickerOverlay from './TextPickerOverlay/TextPickerOverlay'
import { useMemoryBook } from '../../../../providers/MemoryBookProvider'
import { getHasDarkBackground } from '../../../../helpers/getHasDarkBackground'

const skeletonTextPickerContainerCSS = css`
  display: flex;
  flex-direction: column;
  gap: 5px;
  position: relative;
  width: 100%;
  height: 100%;
  @media print {
    width: 100%;
  }
`
const skeletonTextPickerBarCSS = (hasDarkBackground) => css`
  background-color: ${hasDarkBackground ? colors.gray80 : colors.gray03};
  width: 100%;
  height: 30px;
  position: relative;
  flex-grow: 1;
`

const skeletonBarsWrapperCSS = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
  position: relative;
  button:focus & {
    outline: 3px solid black;
    outline-offset: 2px;
  }
`

const skeletonPencilIconWrapperCSS = css`
  position: absolute;
  top: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;

  @media print {
    p {
      display: none;
    }
  }
`

const skeletonButtonCSS = css`
  all: unset;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 99%;
  cursor: pointer;

  @media print {
    background-image: none;
    padding: 0;
  }
`

export default function TextPickerSkeleton({
  overlay,
  positionIndex,
  pageNumber,
  handleMemoryUpdate,
  customLayoutElementId,
  onUpdateCustomLayoutElementData,
  dimensions,
  characterLimits,
}) {
  const [t] = useTranslation()
  const { memoryBook } = useMemoryBook()
  const hasDarkBackground = getHasDarkBackground(pageNumber, memoryBook)

  const skeletonTextPickerBars = []
  // Calculate number of bars dynamically based on container height
  // Each bar: 30px height, 5px gap between bars
  // With n bars: total height = (n × 30px) + ((n-1) × 5px) = 35n - 5
  // Solving for n: numBars = Math.floor((height + 5) / 35)
  let numberOfBars
  if (dimensions?.height) {
    numberOfBars = Math.max(1, Math.floor((dimensions.height + 5) / 35))
  } else {
    // Default fallback if no dimensions provided
    numberOfBars = (characterLimits?.lineCap ?? 12) * 0.75
  }

  for (let i = 0; i < numberOfBars; i++) {
    skeletonTextPickerBars.push(<div key={i} aria-hidden="true" css={skeletonTextPickerBarCSS(hasDarkBackground)} />)
  }

  return (
    <>
      <button
        css={[skeletonButtonCSS, skeletonTextPickerContainerCSS]}
        onClick={overlay.handleClick}
        aria-label={t('TextPicker.openEditor.label', 'Open Story Editor')}
        type="button"
        data-testid="text-picker-skeleton"
      >
        <div css={skeletonBarsWrapperCSS}>
          <div css={skeletonPencilIconWrapperCSS}>
            {hasDarkBackground ? (
              <WhiteTheme>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <ContentCompose size="md" />
                  <Paragraph size="xs">{t('TextPicker.addText.label', 'Add Text')}</Paragraph>
                </div>
              </WhiteTheme>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <ContentCompose size="md" />
                <Paragraph size="xs">{t('TextPicker.addText.label', 'Add Text')}</Paragraph>
              </div>
            )}
          </div>
          {skeletonTextPickerBars}
        </div>
      </button>
      {(overlay.isOpen || overlay.transitioning) && (
        <TextPickerOverlay
          overlay={overlay}
          positionIndex={positionIndex}
          pageNumber={pageNumber}
          handleMemoryUpdate={handleMemoryUpdate}
          customLayoutElementId={customLayoutElementId}
          onUpdateCustomLayoutElementData={onUpdateCustomLayoutElementData}
        />
      )}
    </>
  )
}
